import type { ComponentType } from 'react';
import type { ContentSchema } from '@locusly/types';

export type DashboardModuleKey =
  | 'overview'
  | 'editor'
  | 'blog'
  | 'leads'
  | 'guests'
  | 'analytics'
  | 'domains'
  | 'settings'
  | 'feedback'
  | 'cookies';

export type AnalyticsMetricKey =
  | 'pageViews'
  | 'leadsSubmitted'
  | 'rsvpsSubmitted'
  | 'phoneClicks'
  | 'whatsappClicks'
  | 'ctaClicks'
  | 'directionsClicks';

export type AnalyticsEventKey =
  | 'page_view'
  | 'lead_submitted'
  | 'rsvp_submitted'
  | 'phone_click'
  | 'whatsapp_click'
  | 'cta_click'
  | 'directions_click';

export type ClientAnalyticsConfig = {
  metrics: AnalyticsMetricKey[];
  trackedEvents: AnalyticsEventKey[];
};

export type SiteRendererProps = {
  tenantId: string;
  organizationId: string;
  siteAppKey: string;
  domain: string;
  content: Record<string, Record<string, unknown>>;
  /** Public route being rendered, e.g. "/", "/pt", "/en". */
  routePath?: string;
  /** True when rendered inside the dashboard inline editor. Default false. */
  editing?: boolean;
  /** Signed public token required for unauthenticated public write APIs. */
  publicWriteToken?: string;
};

export type SiteRendererModule = {
  default: ComponentType<SiteRendererProps>;
};

export type ContentSchemaModule = {
  contentSchema: ContentSchema;
};

export type DefaultContentModule = {
  defaultContent?: Record<string, Record<string, unknown>>;
  default?: Record<string, Record<string, unknown>>;
  upgradeContent?: ContentUpgradeFn;
};

export type ContentUpgradeFn = (
  current: Record<string, Record<string, unknown>>,
  defaults: Record<string, Record<string, unknown>>,
) => Record<string, Record<string, unknown>>;

export type AdminRoutesModule = {
  default: ComponentType<{ tenantId: string }>;
};

export type ClientRouteDefinition = {
  path: `/${string}`;
  label: string;
};

export type ClientRegistryEntry = {
  siteRenderer: () => Promise<SiteRendererModule>;
  contentSchema: () => Promise<ContentSchemaModule>;
  defaultContent?: () => Promise<DefaultContentModule>;
  adminRoutes?: () => Promise<AdminRoutesModule>;
  routes?: readonly ClientRouteDefinition[];
  dashboardModules?: DashboardModuleKey[];
  analytics?: ClientAnalyticsConfig;
};

export type ClientRegistry = Record<string, ClientRegistryEntry>;

export class UnknownClientError extends Error {
  constructor(public readonly siteAppKey: string) {
    super(`Cliente não registado: ${siteAppKey}`);
    this.name = 'UnknownClientError';
  }
}

export function resolveClient(
  registry: ClientRegistry,
  siteAppKey: string,
): ClientRegistryEntry {
  const entry = registry[siteAppKey];
  if (!entry) {
    throw new UnknownClientError(siteAppKey);
  }
  return entry;
}
